import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const PAYMENT_MODE_OPTIONS = ['Cash', 'UPI', 'Bank'];

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ExpenseForm = ({ onSubmitted }) => {
  const { user } = useAuth();
  const toast = useToast();

  const [natures, setNatures] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [files, setFiles] = useState([]);

  const [form, setForm] = useState({
    date: todayISO(),
    expenseNature: '',
    contractorName: '',
    projectName: '',
    billNumber: '',
    amount: '',
    paymentMode: 'Cash',
    paidBy: '',
    paidAmount: '',
    remarks: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [n, c, p] = await Promise.all([
          axios.get('/api/sheet/natures'),
          axios.get('/api/sheet/contractors'),
          axios.get('/api/sheet/projects'),
        ]);
        setNatures(n.data);
        setContractors(c.data);
        setProjects(p.data);
      } catch (e) {
        /* ignore */
      }
    })();
  }, []);

  const balance = useMemo(
    () => (parseFloat(form.amount) || 0) - (parseFloat(form.paidAmount) || 0),
    [form.amount, form.paidAmount]
  );

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleFileSelect = (selected) => {
    const arr = Array.from(selected || []);
    const valid = arr.filter((f) => {
      const okType = f.type.startsWith('image/') || f.type === 'application/pdf';
      const okSize = f.size <= 10 * 1024 * 1024;
      if (!okType) toast.error(`${f.name} is not a valid file type`);
      if (!okSize) toast.error(`${f.name} exceeds 10MB`);
      return okType && okSize;
    });
    setFiles((prev) => [...prev, ...valid].slice(0, 5));
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const addNature = async () => {
    const name = window.prompt('Enter new expense nature:');
    if (!name || !name.trim()) return;
    try {
      const res = await axios.post('/api/sheet/natures', { name: name.trim() });
      setNatures((prev) => [...prev.filter((x) => x.name !== res.data.name), res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm((prev) => ({ ...prev, expenseNature: res.data.name }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add nature');
    }
  };

  const addContractor = async () => {
    const name = window.prompt('Enter new contractor name:');
    if (!name || !name.trim()) return;
    try {
      const res = await axios.post('/api/sheet/contractors', { name: name.trim() });
      setContractors((prev) => [...prev.filter((x) => x.name !== res.data.name), res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm((prev) => ({ ...prev, contractorName: res.data.name }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add contractor');
    }
  };

  const addProject = async () => {
    const name = window.prompt('Enter new project name:');
    if (!name || !name.trim()) return;
    try {
      const res = await axios.post('/api/sheet/projects', { name: name.trim() });
      setProjects((prev) => [...prev.filter((x) => x.name !== res.data.name), res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm((prev) => ({ ...prev, projectName: res.data.name }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add project');
    }
  };

  const resetForm = () => {
    setForm({ date: todayISO(), expenseNature: '', contractorName: '', projectName: '', billNumber: '', amount: '', paymentMode: 'Cash', paidBy: '', paidAmount: '', remarks: '' });
    setFiles([]);
    setSubmitted(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.date || !form.expenseNature || !form.contractorName || form.amount === '' || form.amount === null) {
      return setError('Date, Expense Nature, Contractor Name and Amount are required');
    }
    if (parseFloat(form.amount) < 0) return setError('Amount cannot be negative');
    if (parseFloat(form.paidAmount) < 0) return setError('Paid amount cannot be negative');

    setSubmitting(true);
    try {
      const data = new FormData();
      data.append('date', form.date);
      data.append('expenseNature', form.expenseNature);
      data.append('contractorName', form.contractorName);
      data.append('projectName', form.projectName);
      data.append('billNumber', form.billNumber);
      data.append('amount', form.amount);
      data.append('paymentMode', form.paymentMode);
      data.append('paidBy', form.paidBy);
      data.append('paidAmount', form.paidAmount);
      data.append('remarks', form.remarks);
      files.forEach((f) => data.append('proof', f));

      const res = await axios.post('/api/employee-expenses', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSubmitted(res.data);
      toast.success('Expense submitted successfully');
      if (onSubmitted) onSubmitted(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit expense');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(v) || 0);

  if (submitted) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 16px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✅</div>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '8px' }}>Expense Submitted</h2>
        <p style={{ color: 'var(--gray-500)', marginBottom: '20px' }}>Your expense was saved successfully.</p>
        <div style={{ margin: '0 auto 20px', maxWidth: '360px', padding: '16px', background: 'var(--gray-100)', borderRadius: '8px' }}>
          <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>Expense ID</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--primary)' }}>{submitted.expenseId}</div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: '8px' }}>
            Amount {formatCurrency(submitted.amount)} · Balance {formatCurrency(submitted.balanceAmount)}
          </div>
        </div>
        <button className="btn btn-outline" onClick={resetForm}>Submit Another</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-error">{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="form-group">
          <label className="form-label">Employee ID</label>
          <input type="text" className="form-input" value={user?.employeeId || user?.email || ''} disabled style={{ background: 'var(--gray-100)' }} />
        </div>
        <div className="form-group">
          <label className="form-label">Employee Name</label>
          <input type="text" className="form-input" value={user?.name || ''} disabled style={{ background: 'var(--gray-100)' }} />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Date *</label>
        <input type="date" name="date" className="form-input" value={form.date} onChange={handleChange} required />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'end' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Expense Nature *</label>
          <select name="expenseNature" className="form-select" value={form.expenseNature} onChange={handleChange} required>
            <option value="">Select nature</option>
            {natures.map((n) => <option key={n._id} value={n.name}>{n.name}</option>)}
          </select>
        </div>
        <button type="button" className="btn btn-outline btn-sm" onClick={addNature} title="Add new nature">+</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'end', marginTop: '16px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Contractor Name *</label>
          <select name="contractorName" className="form-select" value={form.contractorName} onChange={handleChange} required>
            <option value="">Select contractor</option>
            {contractors.map((c) => <option key={c._id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <button type="button" className="btn btn-outline btn-sm" onClick={addContractor} title="Add new contractor">+</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'end', marginTop: '16px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Project Name</label>
          <select name="projectName" className="form-select" value={form.projectName} onChange={handleChange}>
            <option value="">Select project</option>
            {projects.map((p) => <option key={p._id} value={p.name}>{p.name}</option>)}
          </select>
        </div>
        <button type="button" className="btn btn-outline btn-sm" onClick={addProject} title="Add new project">+</button>
      </div>

      <div className="form-group">
        <label className="form-label">Bill Number</label>
        <input type="text" name="billNumber" className="form-input" placeholder="e.g. INV-1234" value={form.billNumber} onChange={handleChange} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="form-group">
          <label className="form-label">Amount (₹) *</label>
          <input type="number" name="amount" className="form-input" placeholder="0.00" step="0.01" min="0" value={form.amount} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label className="form-label">Payment Mode</label>
          <select name="paymentMode" className="form-select" value={form.paymentMode} onChange={handleChange}>
            {PAYMENT_MODE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="form-group">
          <label className="form-label">Paid By</label>
          <input type="text" name="paidBy" className="form-input" placeholder="Name of payer" value={form.paidBy} onChange={handleChange} />
          <div className="form-hint">Enter SELF, COMPANY, OWNER, or the payer's name</div>
        </div>
        <div className="form-group">
          <label className="form-label">Paid Amount (₹)</label>
          <input type="number" name="paidAmount" className="form-input" placeholder="0.00" step="0.01" min="0" value={form.paidAmount} onChange={handleChange} />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Balance Amount</label>
        <input type="text" className="form-input" value={formatCurrency(balance)} disabled style={{ background: 'var(--gray-100)', fontWeight: 600, color: balance > 0 ? 'var(--danger)' : 'var(--success)' }} />
      </div>

      <div className="form-group">
        <label className="form-label">Expense Proof / Bill Attachment</label>
        <input type="file" multiple accept="image/*,application/pdf" onChange={(e) => handleFileSelect(e.target.files)} className="form-input" />
        <div className="form-hint">Images or PDF, up to 5 files, 10MB each</div>
        {files.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
            {files.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem' }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📎 {f.name}</span>
                <button type="button" className="btn btn-danger btn-sm" onClick={() => removeFile(i)}>Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Remarks</label>
        <textarea name="remarks" className="form-textarea" placeholder="Optional notes..." value={form.remarks} onChange={handleChange} />
      </div>

      <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit Expense'}
        </button>
      </div>
    </form>
  );
};

export default ExpenseForm;
